import { Mail, MessageSquare, Send } from "lucide-react";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { PageContainer } from "@/components/flowly/PageContainer/PageContainer";
import { useLanguage } from "@/contexts/LanguageContext";
import { SUPPORT_EMAIL } from "@/config/constants";
import { useContact } from "./useContact";

const Contact = () => {
  const { t, isRTL } = useLanguage();
  const {
    name,
    setName,
    email,
    setEmail,
    message,
    setMessage,
    isSubmitting,
    handleSubmit,
  } = useContact();

  return (
    <PageContainer className="py-16">
        <div className="text-center mb-12">
          <h1 className="text-4xl font-bold text-foreground mb-4">{t.contact.title}</h1>
          <p className="text-muted-foreground text-lg">
            {t.contact.subtitle}
          </p>
        </div>

        <div className="grid md:grid-cols-2 gap-12">
          {/* Contact Form */}
          <div className="bg-card border border-border rounded-2xl p-8">
            <form onSubmit={handleSubmit} className="space-y-6">
              <div className="space-y-2">
                <Label htmlFor="name">{t.contact.fullName}</Label>
                <Input
                  id="name"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder={t.contact.namePlaceholder}
                  required
                  className="text-start"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="email">{t.contact.email}</Label>
                <Input
                  id="email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="your@email.com"
                  required
                  dir="ltr"
                  className="text-left"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="message">{t.contact.messageLabel}</Label>
                <Textarea
                  id="message"
                  value={message}
                  onChange={(e) => setMessage(e.target.value)}
                  placeholder={t.contact.messagePlaceholder}
                  required
                  rows={5}
                  className="text-start resize-none"
                />
              </div>

              <Button
                type="submit"
                className="w-full"
                disabled={isSubmitting}
              >
                {isSubmitting ? (
                  t.contact.sending
                ) : (
                  <>
                    <Send className={cn("w-4 h-4", isRTL ? "ml-2" : "mr-2")} />
                    {t.contact.sendMessage}
                  </>
                )}
              </Button>
            </form>
          </div>

          {/* Contact Info */}
          <div className="space-y-8">
            <div className="bg-card border border-border rounded-2xl p-8">
              <div className="flex items-center gap-4 mb-4">
                <div className="w-12 h-12 bg-primary/10 rounded-xl flex items-center justify-center">
                  <Mail className="w-6 h-6 text-primary" />
                </div>
                <div>
                  <h3 className="font-semibold text-foreground">{t.contact.emailLabel}</h3>
                  <p className="text-muted-foreground">{SUPPORT_EMAIL}</p>
                </div>
              </div>
            </div>

            <div className="bg-card border border-border rounded-2xl p-8">
              <div className="flex items-center gap-4 mb-4">
                <div className="w-12 h-12 bg-primary/10 rounded-xl flex items-center justify-center">
                  <MessageSquare className="w-6 h-6 text-primary" />
                </div>
                <div>
                  <h3 className="font-semibold text-foreground">{t.contact.responseTime}</h3>
                  <p className="text-muted-foreground">{t.contact.responseHours}</p>
                </div>
              </div>
            </div>

            <div className="bg-gradient-to-br from-primary/10 to-primary/5 border border-primary/20 rounded-2xl p-8">
              <h3 className="font-semibold text-foreground mb-2">{t.contact.faqTitle}</h3>
              <p className="text-muted-foreground text-sm mb-4">
                {t.contact.faqSubtitle}
              </p>
              <Button variant="outline" size="sm" className="w-full">
                {t.contact.faqButton}
              </Button>
            </div>
          </div>
        </div>
    </PageContainer>
  );
};

export default Contact;
